"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";

function useCountUp(end: number, duration = 2000, inView: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let startTime: number | null = null;
    const animate = (t: number) => {
      if (startTime === null) startTime = t;
      const p = Math.min((t - startTime) / duration, 1);
      setCount(Math.floor(p * end));
      if (p < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [end, duration, inView]);
  return count;
}

export default function GlobalNetwork() {
  const [inView, setInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => e.isIntersecting && setInView(true),
      { threshold: 0.3 }
    );
    sectionRef.current && io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);

  const count1 = useCountUp(33, 2000, inView);
  const count2 = useCountUp(200000, 2000, inView);
  const count3 = useCountUp(4100, 2000, inView);
  const count4 = useCountUp(4, 2000, inView);

  return (
    <section
      ref={sectionRef}
      id="network"
      className="relative isolate w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] bg-[#121212] text-white overflow-hidden"
    >
      <div className="relative mx-auto max-w-[1400px] px-[3%] lg:px-[5%] py-12 md:py-24 z-10">
        {/* Background map */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10"
          style={{
            backgroundImage: "url('/World_Map.svg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            filter: "brightness(0.2)",
            WebkitMaskImage:
              "radial-gradient(70% 60% at 50% 35%, black 60%, transparent 100%)",
            maskImage:
              "radial-gradient(70% 60% at 50% 35%, black 60%, transparent 100%)",
          }}
        />

        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-20 opacity-10"
          style={{
            background:
              "radial-gradient(800px 800px at 20% 0%, #1a1a1a 0%, transparent 60%)",
          }}
        />

        {/* Heading */}
        <h3 className="mt-4 text-[clamp(22px,3vw,48px)] tracking-tight">
          Our Services
        </h3>

        <div className="mt-3 md:mt-4 h-[3px] w-20 md:w-24 rounded-full bg-white" />

        {/* FIXED paragraph */}
        <p
          className="
            mt-4 md:mt-6 max-w-3xl
            text-[clamp(15px,1.4vw,18px)]
            leading-[1.65]
            text-white/85
          "
        >
          We support ferrous and non-ferrous casting, CNC machining, molding,
          tube bending, stamping, forging, finishing operations, and mechanical
          assembly/fabrication. Production is based on customer drawings and
          specifications, with material selection, tolerances, and quality
          requirements defined by each application. Quality control includes
          dimensional inspection, material traceability through MTRs, and final
          inspection reports issued to approved drawings and control plans.
        </p>

        {/* Stats */}
        <div className="mt-10 md:mt-14 grid grid-cols-2 gap-y-8 gap-x-8 md:grid-cols-4">
          <div>
            <div className="text-[clamp(28px,3vw,56px)]">{count1}M+</div>
            <p className="mt-2 text-[clamp(12px,1.2vw,16px)] text-white/70">
              Parts manufactured through our network
            </p>
          </div>

          <div>
            <div className="text-[clamp(28px,3vw,56px)]">
              {count2.toLocaleString()}+
            </div>
            <p className="mt-2 text-[clamp(12px,1.2vw,16px)] text-white/70">
              Hours of monthly machine capacity, on-demand
            </p>
          </div>

          <div>
            <div className="text-[clamp(28px,3vw,56px)]">
              {count3.toLocaleString()}+
            </div>
            <p className="mt-2 text-[clamp(12px,1.2vw,16px)] text-white/70">
              Combinations of material, process, finish options
            </p>
          </div>

          <div>
            <div className="text-[clamp(28px,3vw,56px)]">{count4}</div>
            <p className="mt-2 text-[clamp(12px,1.2vw,16px)] text-white/70">
              Supply regions for reduced risk: U.S., China, India, Vietnam
            </p>
          </div>
        </div>

        <Link
          href="/processes"
          className="mt-10 md:mt-14 inline-flex items-center rounded-[30px] bg-[#FDC300] px-[20px] py-[10px] text-[14px] font-semibold text-black shadow-sm hover:brightness-95 transition-all"
        >
          Learn More
          <ArrowUpRight className="ml-2 h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}
